<template>
  <v-container>
    <section class="mt-2 mb-10">
      <h1 v-if="me" class="font-size--24 mt-10">펀다에이아이 집금서비스</h1>
      <p class="font-size--16 color--nice-grey mt-2">서비스 이용료는 총 금액의 0.5%</p>
    </section>

    <section class="bnpl-description mb-10">
      <div class="description-box">
        <p class="font-size--15 font-weight-medium">
          제휴 서비스 업체에 펀다에이아이 대출로 1개월차 이용료를 미리 지급하고, 사장님은 매일 소액 일상환하는 상품입니다.
        </p>
        <p class="font-size--15 font-weight-medium">
          (일 일정금액씩 30일 간 상환하는 일정으로서 상품에 따라 일상환 금액은 달라집니다.)
        </p>
        <p class="font-size--15 font-weight-medium mt-2">
          펀다에이아이의 일반 대출과 동일하게, 공시 후 투자자 모집을 통해 실행되며, 투자금은 서비스 제공업체에 바로 입금됩니다.
        </p>
      </div>
    </section>

    <GetOffer
      :loading.sync="getOfferLoading"
      :offer.sync="offer"
      :options="componentGetOffer.options"
      @update:offer="handleOfferUpdate"
    >
      <div></div>
    </GetOffer>

    <section class="mb-10">
      <p class="font-size--18 color--nice-grey font-weight-bold mb-0">BNPL 대출 금액</p>
      <h1 class="font-size--35 font-weight-black">
        <v-layout :justify-center="!offer || offer.amount === 0">
          <v-fade-transition hide-on-leave>
            <div v-if="isOfferInProgress" class="text-center">
              <v-progress-circular size="36" class="my-10" color="grey" indeterminate />
              <p class="font-size--12 color--nice-grey font-weight-normal">
                한도 산출까지는 수~수십 분이 소요될 수 있습니다. <br />
                이 창을 닫아도 한도 산출은 진행되므로, 잠시 후 다시 확인하셔도 됩니다.
              </p>
            </div>
            <span v-else-if="offer && offer.amount === 0"> - 원 </span>
            <span v-else> {{ commaFormat(offer.amount) }} 원 </span>
          </v-fade-transition>
        </v-layout>
      </h1>
      <p class="font-size--14 color--nice-grey mt-1 mb-0">
        신청하신 집금 서비스의 1개월차 금액 입니다.
      </p>
      <p class="font-size--14 color--nice-grey mt-0">
        지급받으신 금액으로 서비스 비용을 납부하시면 됩니다.
      </p>

      <div v-if="offer && offer.status === 'completed'" class="loan-terms mt-4">
        <div class="term-item">
          <span class="term-label">연금리</span>
          <span class="term-value">{{ formattedInterestRate }}%</span>
        </div>
        <div class="term-item">
          <span class="term-label">플랫폼수수료</span>
          <span class="term-value">{{ formattedCommissionRate }}%</span>
        </div>
        <div class="term-item">
          <span class="term-label">상환 방법</span>
          <span class="term-value">30일간 분할하여 일매출에서 자동 일상환(완제시까지)</span>
        </div>
      </div>
    </section>

    <section class="mb-4">
      <BasicButton v-if="isOfferInProgress" depressed height="48" disabled>
        한도 산출 중
      </BasicButton>

      <div v-else>
        <BasicButton
          depressed
          height="48"
          :disabled="!isLoanRequestable || requestLoanLoading"
          :loading="requestLoanLoading"
          @click="goToCheck"
        >
          서비스 신청하기
        </BasicButton>
      </div>
    </section>

    <section>
      <ul class="font-size--14 color--nice-grey outer">
        <li>펀다에이아이BNPL은 소액 단기 대출로서 '대금지급위임 계약'에 따라 대출금이 서비스 제공업체에 바로 입금됩니다.</li>
        <li>결제 후 매일 매출의 일부가 자동으로 상환되어 편리하게 이용하실 수 있습니다.</li>
      </ul>
    </section>

    <div class="bottom-margin"></div>

    <v-footer fixed>
      <v-layout justify-center>
        <v-btn
          text
          color="grey"
          height="25"
          :disabled="requestLoanLoading"
          style="text-decoration: underline"
          :to="'/'"
        >
          돌아가기
        </v-btn>
      </v-layout>
    </v-footer>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { commaFormat } from '@/filter/number'
import { locationRedirect } from '@/util'

export default {
  data() {
    return {
      offer: null,
      requestLoanLoading: false,
      getOfferLoading: false,
    }
  },
  computed: {
    ...mapState('auth', ['me']),
    ...mapState('contract', { contractCurrent: 'current' }),
    ...mapGetters('loan', ['investingPullRevenue', 'isEarlyPaymentEnabled']),
    componentGetOffer() {
      return {
        options: {
          repayment_period_in_days: 35,
          loan_type: 'BNPL_INSUR',
        },
      }
    },
    isLoanRequestable() {
      return this.offer && this.offer.amount >= 100000
    },
    isOfferInProgress() {
      return !this.offer || this.offer.status === 'in_progress'
    },
    formattedInterestRate() {
      return this.offer?.interestRate ? (this.offer.interestRate * 100).toFixed(1) : '-'
    },
    formattedCommissionRate() {
      return this.offer?.commissionRate ? (this.offer.commissionRate * 100).toFixed(1) : '-'
    },
    formattedRepaymentRatio() {
      return this.offer?.repaymentRatio ? (this.offer.repaymentRatio * 100).toFixed(1) : '-'
    },
  },
  async created() {
    _meta({ titles: ['펀다에이아이 집금 서비스'] })

    try {
      await this.validateUserRequirements1()

      const { requestProcess } = this.$route.query
      if ([true, 'true'].includes(requestProcess)) {
        await this.request()
      }
    } catch (error) {
      console.error('사전 체크 중 오류 발생:', error)
    }
  },
  methods: {
    commaFormat,
    ...mapActions('loan', ['updateOffer']),
    ...mapActions('evaluation', [
      'isKcsAgreements',
      'isHometaxAccounts',
      'getCreditEvaluationsCurrent',
    ]),

    handleOfferUpdate(offer) {
      this.updateOffer(offer)
    },

    async request() {
      await this.$router.push('/loan/funda-bnpl-insur-check')
    },
    async validateUserRequirements1() {
      const creditData = await this.getCreditEvaluationsCurrent()
      if (Object.keys(creditData).length === 0) {
        return this.$router.replace({
          path: '/factoring/confirm',
          query: {
            next: '/loan/funda-bnpl-insur',
            redirectPath: '/loan/funda-bnpl-insur',
          },
        })
      }
    },
    async goToCheck() {
      if (this.contractCurrent?.status !== 'started') {
        await this.$swal.basic.error({
          text: '서비스 신청을 위해서는 카드사 집금계좌 변경이 필요합니다. 한번만 변경하시면 이후 대출은 바로 진행됩니다. 펀다에이아이에 문의해 주세요.',
        })
        return
      }

      const { isConfirmed } = await this.$swal.basic.confirm({
        html: '펀다에이아이 집금 서비스를 신청하시겠습니까?',
      })

      if (!isConfirmed) return

      await this.updateOffer(this.offer)

      await this.$router.push({
        path: '/loan/funda-bnpl-insur-check',
        query: {
          requestProcess: 'true',
          offer: JSON.stringify({
            ...this.componentGetOffer,
            offer: this.offer,
          }),
        },
      })
    },
  },
  components: {
    GetOffer: () => import('@/components/loan/GetOffer.vue'),
  },
}
</script>

<style lang="scss" scoped>
.debug-info {
  margin: 20px;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-family: monospace;
  font-size: 14px;

  p {
    margin: 5px 0;
    color: #666;
  }
}

.loan-terms {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px 20px;

  .term-item {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .term-label {
      color: #666;
      font-size: 14px;
      min-width: 80px;
      margin-right: 12px;

      &::after {
        content: ':';
      }
    }

    .term-value {
      color: #333;
      font-size: 14px;
      font-weight: 500;
    }
  }
}

.bnpl-description {
  padding: 0 8px;

  .description-box {
    background-color: #e3f2fd;
    border-radius: 12px;
    padding: 20px;
    border: 2px solid #1976d2;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);

    .icon-wrapper {
      text-align: left;
      margin-bottom: 12px;
    }

    p {
      color: #1976d2;
      line-height: 1.6;
      margin: 0;
      text-align: left;
    }
  }
}

.bottom-margin {
  height: 100px;
}

.color--red {
  color: #e53935;
}
</style>
